/* eslint-disable no-unused-vars */
import { lazy, Suspense, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import ErrorPage from '@/components/ErrorPage'
import { loginNewStudiesRoutes } from './modules/login-new-studies/routes/LoginNewStudiesRoutes'
import { stabilityMaintenanceRoutes } from './modules/stability-maintenance-module/routes/StabilityMaintenanceRoutes'
import { logManipulationRoutes } from './modules/log-manipulation-module/routes/LogMainpulationRoutes'
import LoadingPage from '@/components/LoadingPage'
import { ProtectedRoute, PublicRoute } from './routerConfig.jsx'
import LabelDesignerDragDrop from './pages/Dp'

//PAGES
const Home = lazy(() => import('@/pages/Home'))
const Login = lazy(() => import('@/pages/Login'))
const Users = lazy(() => import('@/modules/users'))
const Location = lazy(() => import('@/pages/Location'))
const PrintModules = lazy(() => import('@/pages/PrintModules'))
const DataEntryModules = lazy(() => import('@/pages/DataEntryModules'))
const DataAccessEvaluationModules = lazy(() => import('@/pages/DataAccessEvaluationModules'))
const SystemMaintenanceModules = lazy(() => import('@/pages/SystemMaintenanceModules'))
const InProgress = lazy(() => import('@/pages/InProgress'))
const ResetPassword = lazy(() => import('@/pages/ResetPassword'))

// SYSTEM MAINTAINCE MODULES
const AdminFlow = lazy(() => import('@/modules/system-maintenance-module/admin'))
const LocationFlow = lazy(() => import('@/modules/system-maintenance-module/location'))
const UserMaintenanceFlow = lazy(
  () => import('@/modules/system-maintenance-module/userMaintenance')
)
const UserGroupFlow = lazy(() => import('@/modules/system-maintenance-module/userGroup'))
const LicenseManagementFlow = lazy(
  () => import('@/modules/system-maintenance-module/licenseManagement')
)
const SystemAuditTrail = lazy(() => import('@/modules/system-maintenance-module/systemAuditTrail'))
const EditLogReport = lazy(() => import('@/modules/system-maintenance-module/editLogReport'))

//Data Access Evaluation
const GraphicGenerator = lazy(() => import('@/modules/data-access-evaluation/GraphicGenerator'))
const ExpirationMultiple = lazy(() => import('@/modules/data-access-evaluation/ExpirationMultiple'))
const ExpirationSingle = lazy(() => import('@/modules/data-access-evaluation/ExpirationSingle'))
const Sample = lazy(() => import('@/modules/data-access-evaluation/SampleTosample'))
const StudyReport = lazy(() => import('@/modules/data-access-evaluation/StudyReport-OOS'))
const StudyReportOOT = lazy(() => import('@/modules/data-access-evaluation/StudyReport-OOT'))
const ParameterOverlay = lazy(() => import('@/modules/data-access-evaluation/ParameterOverlay'))
const SingleProductByYear = lazy(
  () => import('@/modules/data-access-evaluation/SingleProductByYear')
)
const AccessLogReport = lazy(() => import('@/modules/system-maintenance-module/accessLogReport'))

// Print
const SummaryReportGenerator = lazy(() => import('@/modules/print/SummaryReportGenerator'))
const AnalystReportGenerator = lazy(() => import('@/modules/print/AnalystReportGenerator'))
const StabilityDatabaseLogs = lazy(() => import('@/modules/print/StabilityDatabaseLogs'))
const LabelManager = lazy(() => import('@/modules/print/manage-labels/LabelManager'))
const LabelRequest = lazy(() => import('@/modules/print/manage-labels/LabelRequest'))
const LabelPrinting = lazy(() => import('@/modules/print/manage-labels/LabelPrinting'))
const LabelPrintPreview = lazy(() => import('@/modules/print/manage-labels/LabelPrintPreview'))
const RegressionReport = lazy(() => import('@/modules/print/regression-reports/RegressionReport'))
const MultipleRegressionReport = lazy(
  () => import('@/modules/print/regression-reports/MultipleRegressionReport')
)
const StabilityProtocolReport = lazy(() => import('@/modules/print/StabilityProtocolReport'))
const COfAReport = lazy(() => import('@/modules/print/cofa'))
const CdsForm = lazy(() => import('@/modules/data-entry-module/cdsForm'))
const DestructionForm = lazy(() => import('@/modules/data-entry-module/destructionForm'))
const DataEntry = lazy(() => import('@/modules/data-entry-module/dataEntry'))
const DataEntryEditLogReport = lazy(() => import('@/modules/data-entry-module/editLogReport'))

const routes = [
  // {
  //   path: '/login',
  //   element: <PublicRoute />,
  //   children: [
  //     {
  //       index: true,
  //       element: (
  //         <Suspense fallback={<LoadingPage />}>
  //           <Login />
  //         </Suspense>
  //       )
  //     }
  //   ]
  // },

  {
    path: '/login',
    element: (
      <Suspense fallback={<LoadingPage />}>
        <Login />
      </Suspense>
    )
  },

  // Label Print Preview - Auto-print popup (standalone, outside protected routes)
  {
    path: '/label-print-preview',
    element: (
      <Suspense fallback={<LoadingPage />}>
        <LabelPrintPreview />
      </Suspense>
    )
  },

  {
    path: '/drag',
    element: <PublicRoute />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingPage />}>
            <LabelDesignerDragDrop />
          </Suspense>
        )
      }
    ]
  },

  {
    path: '/location',
    element: (
      <Suspense fallback={<LoadingPage />}>
        <Location />
      </Suspense>
    )
  },

  // Protected routes (everything else)
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingPage />}>
            <Home />
          </Suspense>
        ),
        errorElement: <ErrorPage />
      },
      {
        path: '/users',
        element: (
          <Suspense fallback={<LoadingPage />}>
            <Users />
          </Suspense>
        ),
        errorElement: <ErrorPage />
      },

      // Include modular route groups
      loginNewStudiesRoutes,
      stabilityMaintenanceRoutes,
      logManipulationRoutes,

      //Reset Password
      {
        path: '/resetPassword',
        element: (
          <Suspense fallback={<LoadingPage />}>
            <ResetPassword />
          </Suspense>
        )
      },

      // DATA ENTRY MODULES
      {
        path: '/dataEntryModules',
        element: (
          <Suspense fallback={<LoadingPage />}>
            <DataEntryModules />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
        children: [
          {
            path: 'dataEntry',
            element: <DataEntry title="Data Entry" stage="data_entry" />,
            errorElement: <ErrorPage />
          },
          {
            path: 'approvedDataEntryEdit',
            element: <DataEntry title="Approved Data Entry Edit" stage="approved_edit" />,
            errorElement: <ErrorPage />
          },
          {
            path: 'level1ApprovalData',
            element: <DataEntry title="Level 1 Approved Data Entry Edit" stage="level1_approval" />,
            errorElement: <ErrorPage />
          },
          {
            path: 'finalApprovalData',
            element: <DataEntry title="Final Approved Data Entry Edit" stage="final_approval" />,
            errorElement: <ErrorPage />
          },
          {
            path: 'cdsForm',
            element: <CdsForm />,
            errorElement: <ErrorPage />
          },
          {
            path: 'destructionForm',
            element: <DestructionForm />,
            errorElement: <ErrorPage />
          },
          {
            path: 'editLogReport',
            element: <DataEntryEditLogReport />,
            errorElement: <ErrorPage />
          }
        ]
      },

      // DATA ACCESS EVALUATION MODULES
      {
        path: '/dataAccessEvaluationModules',
        element: (
          <Suspense fallback={<LoadingPage />}>
            <DataAccessEvaluationModules />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
        children: [
          {
            path: 'graphicsGenerator',
            element: <GraphicGenerator />,
            errorElement: <ErrorPage />
          },
          {
            path: 'singleProductByYear',
            element: <SingleProductByYear />,
            errorElement: <ErrorPage />
          },
          {
            path: 'sampleToSample',
            element: <Sample />,
            errorElement: <ErrorPage />
          },
          {
            path: 'parameterOverlay',
            element: <ParameterOverlay />,
            errorElement: <ErrorPage />
          },
          {
            path: 'expirationSingleStudy',
            element: <ExpirationSingle />,
            errorElement: <ErrorPage />
          },
          {
            path: 'expirationMultipleStudies',
            element: <ExpirationMultiple />,
            errorElement: <ErrorPage />
          },
          {
            path: 'studyResultsOOSReport',
            element: <StudyReport />,
            errorElement: <ErrorPage />
          },
          {
            path: 'studyResultsOOTReport',
            element: <StudyReportOOT />,
            errorElement: <ErrorPage />
          }
        ]
      },

      // PRINT MODULES
      {
        path: '/printModules',
        element: (
          <Suspense fallback={<LoadingPage />}>
            <PrintModules />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
        children: [
          {
            path: 'summaryReportGenerator',
            element: <SummaryReportGenerator />,
            errorElement: <ErrorPage />
          },
          {
            path: 'analystReportGenerator',
            element: <AnalystReportGenerator />,
            errorElement: <ErrorPage />
          },
          {
            path: 'stabilityDatabaseLogs',
            element: <StabilityDatabaseLogs />,
            errorElement: <ErrorPage />
          },
          {
            path: 'labelManager',
            element: <LabelManager />,
            errorElement: <ErrorPage />
          },
          {
            path: 'labelRequest',
            element: <LabelRequest />,
            errorElement: <ErrorPage />
          },
          {
            path: 'labelPrinting',
            element: <LabelPrinting />,
            errorElement: <ErrorPage />
          },
          {
            path: 'regressionReport',
            element: <RegressionReport />,
            errorElement: <ErrorPage />
          },
          {
            path: 'multipleRegressionReport',
            element: <MultipleRegressionReport />,
            errorElement: <ErrorPage />
          },
          {
            path: 'stabilityProtocolReport',
            element: <StabilityProtocolReport />,
            errorElement: <ErrorPage />
          },
          {
            path: 'cofa',
            element: <COfAReport />,
            errorElement: <ErrorPage />
          }
        ]
      },

      // SYSTEM MAINTENANCE MODULES
      {
        path: '/systemMaintenanceModules',
        element: (
          <Suspense fallback={<LoadingPage />}>
            <SystemMaintenanceModules />
          </Suspense>
        ),
        errorElement: <ErrorPage />,
        children: [
          {
            path: 'admin',
            element: <AdminFlow />,
            errorElement: <ErrorPage />
          },
          {
            path: 'location',
            element: <LocationFlow />,
            errorElement: <ErrorPage />
          },
          {
            path: 'userMaintenance',
            element: <UserMaintenanceFlow />,
            errorElement: <ErrorPage />
          },
          {
            path: 'userGroup',
            element: <UserGroupFlow />,
            errorElement: <ErrorPage />
          },
          {
            path: 'licenseManagement',
            element: <LicenseManagementFlow />,
            errorElement: <ErrorPage />
          },
          {
            path: 'systemAuditTrail',
            element: <SystemAuditTrail />,
            errorElement: <ErrorPage />
          },
          {
            path: 'accessLogReport',
            element: <AccessLogReport />,
            errorElement: <ErrorPage />
          },
          {
            path: 'editLogReport',
            element: <EditLogReport />,
            errorElement: <ErrorPage />
          }
        ]
      },
      {
        path: '/inProgress',
        element: (
          <Suspense fallback={<LoadingPage />}>
            <InProgress />
          </Suspense>
        )
      },
      {
        path: 'testing12345',
        element: (
          <Suspense fallback={<LoadingPage />}>
            <Users />
          </Suspense>
        ),
        errorElement: <ErrorPage />
      },

      // Catch-all route - redirect to home
      {
        path: '*',
        element: <Navigate to="/" replace />
      }
    ]
  }
]

export default routes
